import type { ProductRevenue, RevenueReport, RevenueTotals } from "@cleanbudget/contract/revenue";
import { Card, CardContent, CardHeader, CardTitle } from "@cleanbudget/ui/components/card";
import { Input } from "@cleanbudget/ui/components/input";
import {
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableRow,
} from "@cleanbudget/ui/components/table";
import { cn } from "@cleanbudget/ui/lib/utils";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, stripSearchParams, useNavigate } from "@tanstack/react-router";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/back-link";
import { HelpButton } from "@/components/help/help-button";
import type { HelpTopicId } from "@/components/help/topics";
import { PageHeader } from "@/components/page-header";
import { formatCalendarDate } from "@/lib/format";
import { formatCents } from "@/lib/money";
import { orpc } from "@/lib/orpc";
import { type RevenuePreset, RevenueSearch, rangeOf } from "./-components/range";
import { businessColor, consumerColor, RevenueChart } from "./-components/revenue-chart";

const reportQuery = (range: { from: string; to: string }) =>
	orpc.revenue.report.queryOptions({ input: range });

const presets: readonly Exclude<RevenuePreset, "custom">[] = ["today", "week", "month", "year"];

/**
 * Omzet (spec, story 50): what was paid or put on account, excl VAT, minus refunds on the day of
 * the refund, split into consumer and business, shipping apart. A range (a preset or two dates,
 * in the URL), the totals, a bar per day and a table per product.
 */
export const Route = createFileRoute("/_admin/revenue/")({
	validateSearch: RevenueSearch,
	search: { middlewares: [stripSearchParams({ preset: "month" })] },
	loaderDeps: ({ search }) => rangeOf(search),
	loader: ({ context, deps }) => context.queryClient.ensureQueryData(reportQuery(deps)),
	component: RevenuePage,
});

function RevenuePage() {
	const { t } = useTranslation("revenue");
	const search = Route.useSearch();
	const range = rangeOf(search);
	const { data } = useSuspenseQuery(reportQuery(range));

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				back={<BackLink to="/">{t("back")}</BackLink>}
				title={t("title")}
				description={t("description")}
				help="revenue:revenue"
			/>
			<RangePicker preset={search.preset} from={range.from} to={range.to} />
			<Totals totals={data.total} />
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-1.5">
						<h2>{t("perDay")}</h2>
						<HelpButton topic="revenue:perDay" />
					</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-4">
					<RevenueChart days={data.days} />
					<details className="text-sm">
						<summary className="cursor-pointer text-muted-foreground">{t("dayTable")}</summary>
						<DayTable report={data} />
					</details>
				</CardContent>
			</Card>
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-1.5">
						<h2>{t("perProduct")}</h2>
						<HelpButton topic="revenue:perProduct" />
					</CardTitle>
				</CardHeader>
				<CardContent>
					<ProductTable report={data} />
				</CardContent>
			</Card>
		</div>
	);
}

function RangePicker({ preset, from, to }: { preset: RevenuePreset; from: string; to: string }) {
	const { t } = useTranslation("revenue");
	const navigate = useNavigate({ from: Route.fullPath });
	const fromId = useId();
	const toId = useId();
	const setCustom = (next: { from: string; to: string }) => {
		if (next.from === "" || next.to === "") return;
		void navigate({ search: { preset: "custom", ...next } });
	};
	return (
		<div className="flex flex-wrap items-end gap-3">
			<div className="flex items-center gap-1.5">
				<nav aria-label={t("range.label")} className="flex flex-wrap gap-1 rounded-lg bg-muted p-1">
					{presets.map((option) => (
						<Link
							key={option}
							to="/revenue"
							search={{ preset: option }}
							aria-current={preset === option ? "page" : undefined}
							className={cn(
								"rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground",
								preset === option && "bg-background text-foreground shadow-sm",
							)}
						>
							{t(`range.${option}`)}
						</Link>
					))}
				</nav>
				<HelpButton topic="revenue:period" />
			</div>
			<div className="flex flex-wrap items-end gap-2">
				<label htmlFor={fromId} className="flex flex-col gap-1 text-sm text-muted-foreground">
					{t("range.from")}
					<Input
						id={fromId}
						type="date"
						value={from}
						max={to}
						onChange={(event) => setCustom({ from: event.target.value, to })}
						className="w-40"
					/>
				</label>
				<label htmlFor={toId} className="flex flex-col gap-1 text-sm text-muted-foreground">
					{t("range.to")}
					<Input
						id={toId}
						type="date"
						value={to}
						min={from}
						onChange={(event) => setCustom({ from, to: event.target.value })}
						className="w-40"
					/>
				</label>
			</div>
		</div>
	);
}

/** The headline figures: the total, then consumer, business and the shipping inside them. */
function Totals({ totals }: { totals: RevenueTotals }) {
	const { t } = useTranslation("revenue");
	const consumer = totals.consumer.goods + totals.consumer.shipping;
	const business = totals.business.goods + totals.business.shipping;
	const shipping = totals.consumer.shipping + totals.business.shipping;
	const tiles: readonly { key: string; label: string; value: number; swatch?: string; help?: HelpTopicId }[] =
		[
			{ key: "total", label: t("total"), value: totals.total, help: "revenue:counts" },
			{
				key: "consumer",
				label: t("consumer"),
				value: consumer,
				swatch: consumerColor,
				help: "revenue:segments",
			},
			{ key: "business", label: t("business"), value: business, swatch: businessColor },
			{ key: "shipping", label: t("shipping"), value: shipping },
		];
	return (
		<dl className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="revenue-totals">
			{tiles.map((tile) => (
				<div key={tile.key} className="flex flex-col gap-1 rounded-2xl p-4 ring-1 ring-foreground/10">
					<dt className="flex items-center gap-1.5 text-sm text-muted-foreground">
						{tile.swatch ? <span className={cn("size-2.5 rounded-sm", tile.swatch)} aria-hidden /> : null}
						{tile.label}
						{tile.help ? <HelpButton topic={tile.help} /> : null}
					</dt>
					<dd className="font-heading text-xl font-semibold tabular-nums sm:text-2xl">
						{formatCents(tile.value)}
					</dd>
				</div>
			))}
			<p className="col-span-full text-xs text-muted-foreground">{t("exclVat")}</p>
		</dl>
	);
}

function DayTable({ report }: { report: RevenueReport }) {
	const { t } = useTranslation("revenue");
	return (
		<Table className="mt-3">
			<TableHeader>
				<TableRow>
					<TableHead>{t("column.date")}</TableHead>
					<TableHead className="text-right">{t("consumer")}</TableHead>
					<TableHead className="text-right">{t("business")}</TableHead>
					<TableHead className="text-right">{t("column.shipping")}</TableHead>
					<TableHead className="text-right">{t("total")}</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{report.days.map((day) => (
					<TableRow key={day.date}>
						<TableCell>{formatCalendarDate(day.date)}</TableCell>
						<TableCell className="text-right tabular-nums">
							{formatCents(day.consumer.goods + day.consumer.shipping)}
						</TableCell>
						<TableCell className="text-right tabular-nums">
							{formatCents(day.business.goods + day.business.shipping)}
						</TableCell>
						<TableCell className="text-right tabular-nums">
							{formatCents(day.consumer.shipping + day.business.shipping)}
						</TableCell>
						<TableCell className="text-right font-medium tabular-nums">{formatCents(day.total)}</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
}

function ProductTable({ report }: { report: RevenueReport }) {
	const { t } = useTranslation("revenue");
	if (report.products.length === 0 && report.total.total === 0) {
		return <p className="text-muted-foreground">{t("empty")}</p>;
	}
	const shipping = report.total.consumer.shipping + report.total.business.shipping;
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>{t("column.product")}</TableHead>
					<TableHead className="text-right">{t("consumer")}</TableHead>
					<TableHead className="hidden text-right sm:table-cell">{t("business")}</TableHead>
					<TableHead className="text-right">{t("total")}</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{report.products.map((product: ProductRevenue) => (
					<TableRow key={product.productId}>
						<TableCell className="whitespace-normal">
							<Link
								to="/product/$productId"
								params={{ productId: product.productId }}
								className="font-medium hover:underline"
							>
								{product.name}
							</Link>
						</TableCell>
						<TableCell className="text-right tabular-nums">{formatCents(product.consumer)}</TableCell>
						<TableCell className="hidden text-right tabular-nums sm:table-cell">
							{formatCents(product.business)}
						</TableCell>
						<TableCell className="text-right font-medium tabular-nums">
							{formatCents(product.total)}
						</TableCell>
					</TableRow>
				))}
				<TableRow>
					<TableCell className="text-muted-foreground">{t("shippingLine")}</TableCell>
					<TableCell className="text-right tabular-nums">
						{formatCents(report.total.consumer.shipping)}
					</TableCell>
					<TableCell className="hidden text-right tabular-nums sm:table-cell">
						{formatCents(report.total.business.shipping)}
					</TableCell>
					<TableCell className="text-right font-medium tabular-nums">{formatCents(shipping)}</TableCell>
				</TableRow>
			</TableBody>
			<TableFooter>
				<TableRow>
					<TableCell>{t("total")}</TableCell>
					<TableCell className="text-right tabular-nums">
						{formatCents(report.total.consumer.goods + report.total.consumer.shipping)}
					</TableCell>
					<TableCell className="hidden text-right tabular-nums sm:table-cell">
						{formatCents(report.total.business.goods + report.total.business.shipping)}
					</TableCell>
					<TableCell className="text-right tabular-nums">{formatCents(report.total.total)}</TableCell>
				</TableRow>
			</TableFooter>
		</Table>
	);
}
